/* Column widths for the cockpit's grids.
 *
 * scale-data-grid's own auto-sizing gets two things wrong: it picks the widest
 * row by `String(value).length`, so every tags/link cell ties at
 * `[object Object]`, and it never measures the header. Both show up as clipped
 * text. So we measure instead — a canvas text metric at the font the cell draws
 * in — and hand the grid an explicit `width`, which it then leaves alone.
 *
 * All widths here are content widths; the grid adds 32px of padding and margin
 * on top. */

/** `.thead__text` — Scale's small step, bold from the `th`. */
const HEADER = { size: 12, bold: true };
/** A body cell, at the row size set in the grid's injected styles. */
const BODY = { size: 14, bold: false };
/** scale-tag at the `small` size the tags cell always asks for. */
const TAG = { size: 12, bold: true };
/** A row action's label, on scale-button at `small`. Measured bold and at the
    row size rather than the button's own caption step: the actions cell is the
    one column whose content is a control, and a clipped verb on a control is
    worse than a column two pixels wider than it needed to be. */
const BUTTON = { size: 14, bold: true };

/** scale-tag's 6px either side of its label. */
const TAG_PADDING = 12;
/** `.tbody__tag-list li` margin, between tags but not after the last. */
const TAG_GAP = 8;

/** scale-button's `--spacing-x-*-small`, both sides. */
const BUTTON_PADDING = 24;
/** `.tbody__actions scale-button` margin-right, which Scale puts after every
    button including the last one. */
const BUTTON_GAP = 8;

/** Covers the rounding between a text metric and the laid-out integer width. */
const SLACK = 2;

/** Per-character advance where there is no canvas — jsdom, chiefly. Generous on
    purpose: too wide is a wide column, too narrow is clipped text. */
const FALLBACK_ADVANCE = 0.7;

interface Face {
  readonly size: number;
  readonly bold: boolean;
}

/** `undefined` = not asked yet, `null` = no 2D canvas in this environment. */
let context: CanvasRenderingContext2D | null | undefined;

function measurer(): CanvasRenderingContext2D | null {
  if (context === undefined) {
    context = document.createElement('canvas').getContext('2d');
  }
  return context;
}

/* Nothing is cached: the family is read live, so a width measured against the
   fallback stack is not kept once the web font arrives. */
function width(text: string, face: Face): number {
  const canvas = measurer();
  if (!canvas) return text.length * face.size * FALLBACK_ADVANCE;

  const family = getComputedStyle(document.body).fontFamily;
  canvas.font = `${face.bold ? '700 ' : ''}${face.size}px ${family}`;
  return canvas.measureText(text).width;
}

function widest(values: Iterable<string>, face: Face): number {
  let largest = 0;
  for (const value of values) {
    largest = Math.max(largest, width(value, face));
  }
  return largest;
}

/** What a column header needs to print in full. */
export function headerWidth(header: string): number {
  return width(header, HEADER);
}

/** What the widest of a column's plain values needs — text, date, number, and
    link labels, all of which draw at the row size. */
export function textWidth(values: Iterable<string>): number {
  return widest(values, BODY);
}

/** The widest tag row: every chip, its padding, and the gaps between them. */
export function tagsWidth(rows: Iterable<Iterable<{ readonly content: string }>>): number {
  let largest = 0;
  for (const tags of rows) {
    let row = 0;
    let count = 0;
    for (const tag of tags) {
      row += width(tag.content, TAG) + TAG_PADDING;
      count += 1;
    }
    largest = Math.max(largest, row + Math.max(0, count - 1) * TAG_GAP);
  }
  return largest;
}

/** A square icon-only button: 32px (`--min-height` at `small`) for the hit
    target, plus the trailing gap Scale adds after every button. */
const ICON_ONLY_BUTTON = 32;

/** The widest row of buttons: every label, its padding, and Scale's trailing
    margin on each one. An empty label means icon-only — the button is a fixed
    32px square rather than a text-measured width. */
export function actionsWidth(rows: Iterable<Iterable<{ readonly label: string }>>): number {
  let largest = 0;
  for (const actions of rows) {
    let row = 0;
    for (const action of actions) {
      if (action.label === '') {
        row += ICON_ONLY_BUTTON + BUTTON_GAP;
      } else {
        row += width(action.label, BUTTON) + BUTTON_PADDING + BUTTON_GAP;
      }
    }
    largest = Math.max(largest, row);
  }
  return largest;
}

/** The width to hand the grid, given everything the column has to hold. */
export function settle(...candidates: readonly number[]): number {
  return Math.ceil(Math.max(...candidates)) + SLACK;
}
